// Token translation: CoBlend (Tailwind v4) → @shopify/polaris-tokens@9.4.2
//
// Source token values verified from CoBlend resources/css/app.css:
//   @theme block (--font-*, --radius, bridged --color-*),
//   @layer base :root (HSL triplets for shadcn semantic set + app-domain tokens),
//   @layer base .dark (dark-mode overrides),
//   @layer base .theme-{color} (10 per-household palette overrides).
//
// Polaris token values verified from:
//   node_modules/@shopify/polaris-tokens/dist/css/styles.css :root block (v9.4.2).
//
// Classification legend:
//   clean         — same semantic role, compatible values, straightforward rename.
//   standardized  — same semantic role, values differ; Polaris tone system covers it.
//   mismatch      — no Polaris equivalent or fundamentally incompatible model.

import type { TokenRow } from './types'

export const TOKENS: readonly TokenRow[] = [
  // ── Clean maps ───────────────────────────────────────────────────────────────

  {
    id: 'radius',
    sourceToken: '--radius',
    sourceValue: '0.5rem',
    polarisToken: '--p-border-radius-200',
    polarisValue: '0.5rem',
    note: 'Same computed value; naming differs (semantic label vs. numeric scale).',
    classification: 'clean',
  },
  {
    id: 'font-sans',
    sourceToken: '--font-sans',
    sourceValue: "'Inter', system-ui, sans-serif",
    polarisToken: '--p-font-family-sans',
    polarisValue:
      "'Inter', -apple-system, BlinkMacSystemFont, 'San Francisco', 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
    note: 'Both are Inter-first; Polaris appends a longer OS-native fallback chain.',
    classification: 'clean',
  },
  {
    id: 'space-base',
    sourceToken: '--spacing (Tailwind v4 base unit)',
    sourceValue: '0.25rem per step',
    polarisToken: '--p-space-100',
    polarisValue: '0.25rem',
    note: 'Both use 0.25rem as the atomic space unit; naming differs (Tailwind numeric utilities vs. Polaris scale tokens).',
    classification: 'clean',
  },

  // ── Mismatches ───────────────────────────────────────────────────────────────

  {
    id: 'font-display',
    sourceToken: '--font-display',
    sourceValue: "'Sora', 'Inter', system-ui, sans-serif",
    polarisToken: 'none',
    polarisValue: '—',
    note: 'Polaris uses one font family for all text roles; no separate display-font token exists.',
    classification: 'mismatch',
  },
  {
    id: 'font-child',
    sourceToken: '--font-child',
    sourceValue: "'Nunito', system-ui, sans-serif",
    polarisToken: 'none',
    polarisValue: '—',
    note: 'App-domain token for child-facing UI; Polaris has no audience-differentiated font token.',
    classification: 'mismatch',
  },
  {
    id: 'primary',
    sourceToken: '--primary',
    sourceValue: 'hsl(262, 100%, 30%)',
    swatchColor: 'hsl(262, 100%, 30%)',
    polarisToken: '--p-color-bg-fill-brand',
    polarisValue: 'rgba(48, 48, 48, 1)',
    polarisSwatch: 'rgba(48, 48, 48, 1)',
    note: 'Polaris v13 brand fill is neutral dark gray; hue override is not a supported API surface.',
    classification: 'mismatch',
  },
  {
    id: 'secondary',
    sourceToken: '--secondary',
    sourceValue: 'hsl(31, 100%, 60%)',
    swatchColor: 'hsl(31, 100%, 60%)',
    polarisToken: 'none',
    polarisValue: '—',
    note: 'Polaris has a single brand-fill; a second brand accent has no token home.',
    classification: 'mismatch',
  },
  {
    id: 'tenant-palettes',
    sourceToken: '.theme-blue … .theme-indigo (10 classes)',
    sourceValue: 'Override --primary, --background, --card, --muted, --border, --nav',
    polarisToken: 'none',
    polarisValue: '—',
    note: 'Ten per-household palettes swap the entire shadcn semantic set at runtime; Polaris has no tenant-theming API.',
    classification: 'mismatch',
  },
  {
    id: 'points',
    sourceToken: '--points',
    sourceValue: 'hsl(31, 100%, 70%)',
    swatchColor: 'hsl(31, 100%, 70%)',
    polarisToken: 'none',
    polarisValue: '—',
    note: 'App-domain token for chore point display badges; no Polaris equivalent.',
    classification: 'mismatch',
  },
  {
    id: 'reward',
    sourceToken: '--reward',
    sourceValue: 'hsl(31, 100%, 50%)',
    swatchColor: 'hsl(31, 100%, 50%)',
    polarisToken: 'none',
    polarisValue: '—',
    note: 'App-domain token for reward redemption display; no Polaris equivalent.',
    classification: 'mismatch',
  },
  {
    id: 'kid-cash',
    sourceToken: '--kid-cash',
    sourceValue: 'hsl(110, 99%, 30%)',
    swatchColor: 'hsl(110, 99%, 30%)',
    polarisToken: 'none',
    polarisValue: '—',
    note: 'App-domain token for monetary reward display; no Polaris equivalent.',
    classification: 'mismatch',
  },
  {
    id: 'streak',
    sourceToken: '--streak',
    sourceValue: 'hsl(31, 100%, 60%)',
    swatchColor: 'hsl(31, 100%, 60%)',
    polarisToken: 'none',
    polarisValue: '—',
    note: 'App-domain token for activity streak display; no Polaris equivalent.',
    classification: 'mismatch',
  },

  // ── Standardizations ─────────────────────────────────────────────────────────

  {
    id: 'dark-mode',
    sourceToken: '.dark (class on <html>)',
    sourceValue: 'Overrides all HSL triplets to dark-mode values',
    polarisToken: 'color-scheme: dark (.p-theme-dark)',
    polarisValue: 'Polaris dark-mode token set via color-scheme',
    note: 'CoBlend class-switches every semantic token; Polaris provides its own dark-mode values via the color-scheme API.',
    classification: 'standardized',
  },
  {
    id: 'background',
    sourceToken: '--background',
    sourceValue: 'hsl(262, 100%, 95%)',
    swatchColor: 'hsl(262, 100%, 95%)',
    polarisToken: '--p-color-bg-surface',
    polarisValue: 'rgba(255, 255, 255, 1)',
    polarisSwatch: 'rgba(255, 255, 255, 1)',
    note: 'Both are page-surface tokens; CoBlend carries the brand hue, Polaris is neutral white.',
    classification: 'standardized',
  },
  {
    id: 'muted',
    sourceToken: '--muted',
    sourceValue: 'hsl(262, 100%, 90%)',
    swatchColor: 'hsl(262, 100%, 90%)',
    polarisToken: '--p-color-bg-surface-secondary',
    polarisValue: 'rgba(247, 247, 247, 1)',
    polarisSwatch: 'rgba(247, 247, 247, 1)',
    note: 'Both are subdued/muted surface tokens; CoBlend carries brand hue, Polaris is neutral gray.',
    classification: 'standardized',
  },
  {
    id: 'input',
    sourceToken: '--input',
    sourceValue: 'hsl(262, 100%, 95%)',
    swatchColor: 'hsl(262, 100%, 95%)',
    polarisToken: '--p-color-input-bg-surface',
    polarisValue: 'rgba(253, 253, 253, 1)',
    polarisSwatch: 'rgba(253, 253, 253, 1)',
    note: 'Both are input field background tokens; CoBlend carries brand hue, Polaris is near-white.',
    classification: 'standardized',
  },
  {
    id: 'border',
    sourceToken: '--border',
    sourceValue: 'hsl(262, 100%, 90%)',
    swatchColor: 'hsl(262, 100%, 90%)',
    polarisToken: '--p-color-border',
    polarisValue: 'rgba(227, 227, 227, 1)',
    polarisSwatch: 'rgba(227, 227, 227, 1)',
    note: 'Both are default border tokens; CoBlend carries brand hue, Polaris is light gray.',
    classification: 'standardized',
  },
  {
    id: 'ring',
    sourceToken: '--ring',
    sourceValue: 'hsl(262, 100%, 30%)',
    swatchColor: 'hsl(262, 100%, 30%)',
    polarisToken: '--p-color-border-focus',
    polarisValue: 'rgba(0, 91, 211, 1)',
    polarisSwatch: 'rgba(0, 91, 211, 1)',
    note: 'Both mark keyboard focus rings; hues differ significantly — Polaris blue meets WCAG AA contrast on white.',
    classification: 'standardized',
  },
  {
    id: 'destructive',
    sourceToken: '--destructive',
    sourceValue: 'hsl(0, 100%, 50%)',
    swatchColor: 'hsl(0, 100%, 50%)',
    polarisToken: '--p-color-bg-fill-critical',
    polarisValue: 'rgba(199, 10, 36, 1)',
    polarisSwatch: 'rgba(199, 10, 36, 1)',
    note: "Both express 'error/danger'; CoBlend is full-chroma red, Polaris is desaturated dark red.",
    classification: 'standardized',
  },
  {
    id: 'success',
    sourceToken: '--success',
    sourceValue: 'hsl(110, 99%, 30%)',
    swatchColor: 'hsl(110, 99%, 30%)',
    polarisToken: '--p-color-bg-fill-success',
    polarisValue: 'rgba(4, 123, 93, 1)',
    polarisSwatch: 'rgba(4, 123, 93, 1)',
    note: "Both express 'success/positive'; hue shifts from yellow-green (CoBlend) to teal-green (Polaris).",
    classification: 'standardized',
  },
  {
    id: 'warning',
    sourceToken: '--warning',
    sourceValue: 'hsl(57, 100%, 50%)',
    swatchColor: 'hsl(57, 100%, 50%)',
    polarisToken: '--p-color-bg-fill-warning',
    polarisValue: 'rgba(255, 184, 0, 1)',
    polarisSwatch: 'rgba(255, 184, 0, 1)',
    note: "Both express 'warning'; similar warm-yellow hue, Polaris is amber rather than bright yellow.",
    classification: 'standardized',
  },
  {
    id: 'info',
    sourceToken: '--info',
    sourceValue: 'hsl(212, 100%, 40%)',
    swatchColor: 'hsl(212, 100%, 40%)',
    polarisToken: '--p-color-bg-fill-info',
    polarisValue: 'rgba(145, 208, 255, 1)',
    polarisSwatch: 'rgba(145, 208, 255, 1)',
    note: "Both express 'info'; CoBlend uses the dark-blue base, Polaris fill is a light-blue surface value.",
    classification: 'standardized',
  },
  {
    id: 'caution',
    sourceToken: '--caution',
    sourceValue: 'hsl(48, 96%, 53%)',
    swatchColor: 'hsl(48, 96%, 53%)',
    polarisToken: '--p-color-bg-fill-caution',
    polarisValue: 'rgba(255, 230, 0, 1)',
    polarisSwatch: 'rgba(255, 230, 0, 1)',
    note: "Both express 'caution'; similar amber-yellow hue, Polaris is a purer yellow.",
    classification: 'standardized',
  },
]
